// What it remembers, in the open: she should be able to see it, correct it, and delete it.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Top } from '@/components/Top';
import { newId } from '@/lib/bubbles';
import { INTRO_PROMPTS, unknownTopics } from '@/memory/curiosity';
import { useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import { MEMORY_KINDS, type Memory, type MemoryKind } from '@/types';

const KIND_LABEL: Record<MemoryKind, string> = {
  about_me: '关于她',
  person: '人',
  event: '事',
  feeling: '感受',
  preference: '偏好',
  belief: '看法',
  prediction: '预判',
  followup: '待跟进',
};

function Note({ m }: { m: Memory }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(m.text);
  const [kind, setKind] = useState<MemoryKind>(m.kind);
  const [subjects, setSubjects] = useState(m.subjects.join('、'));
  const save = () => {
    void appStore.putMemory({ ...m, text: text.trim(), kind, subjects: subjects.split(/[、,，\s]+/).map((s) => s.trim()).filter(Boolean), updatedAt: Date.now() });
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="note editing">
        <textarea value={text} onChange={(e) => setText(e.target.value)} />
        <div className="rowline">
          <select className="input" style={{ width: 'auto' }} value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)}>
            {MEMORY_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
          <input className="input" style={{ flex: 1 }} value={subjects} placeholder="涉及谁（顿号分隔）" onChange={(e) => setSubjects(e.target.value)} />
          <button type="button" className="btn primary" disabled={!text.trim()} onClick={save}>保存</button>
          <button type="button" className="btn" onClick={() => setEditing(false)}>取消</button>
        </div>
      </div>
    );
  }
  const pending = (m.kind === 'followup' || m.kind === 'prediction') && m.status !== 'done';
  return (
    <div className="note">
      <span className="kind">{KIND_LABEL[m.kind]}{m.importance >= 4 ? ' ★' : ''}</span>
      <div>
        <div className="txt">{m.happenedAt && <span style={{ color: 'var(--muted)' }}>{m.happenedAt} · </span>}{m.text}</div>
        <div className="subj">
          {m.subjects.length > 0 && <span>{m.subjects.join('、')} · </span>}
          {pending ? '还开着' : m.status === 'done' ? '已了结' : ''}{pending || m.status === 'done' ? ' · ' : ''}
          记于 {new Date(m.createdAt).toLocaleDateString('zh-CN')}
          {m.lastRecalledAt ? ` · 上次用到 ${new Date(m.lastRecalledAt).toLocaleDateString('zh-CN')}` : ''}
        </div>
      </div>
      <div className="ops">
        {pending && <button type="button" onClick={() => void appStore.putMemory({ ...m, status: 'done', updatedAt: Date.now() })}>了结</button>}
        <button type="button" onClick={() => setEditing(true)}>改</button>
        <button type="button" onClick={() => { if (window.confirm('忘掉这条？')) void appStore.deleteMemory(m.id); }}>忘掉</button>
      </div>
    </div>
  );
}

function Section({ title, count, items, hint }: { title: string; count?: number; items: Memory[]; hint?: string }) {
  return (
    <section>
      <h2>{title}{count != null && <small>{count}</small>}</h2>
      <div className="card">
        {items.length === 0 ? <p className="hint">{hint ?? '还没有。'}</p> : items.map((m) => <Note key={m.id} m={m} />)}
      </div>
    </section>
  );
}

/** "帮它认识你": a few open prompts, all optional; the answers go into the chat as one message so it can respond and learn. */
function IntroForm({ onDone }: { onDone: () => void }) {
  const s = useSettings();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const filled = INTRO_PROMPTS.filter((p) => answers[p.topic]?.trim());
  const submit = () => {
    const text = `关于我——\n\n${filled.map((p) => `【${p.question}】\n${answers[p.topic].trim()}`).join('\n\n')}`;
    void appStore.send(text);
    onDone();
  };
  return (
    <div className="card">
      <p className="hint">想到什么写什么，写几条都行，空着的跳过。它会像听你说话一样读完，然后记住。</p>
      {INTRO_PROMPTS.map((p) => (
        <div key={p.topic} className="field">
          <label>{p.question}</label>
          <textarea className="input" style={{ minHeight: 56 }} value={answers[p.topic] ?? ''} onChange={(e) => setAnswers({ ...answers, [p.topic]: e.target.value })} />
        </div>
      ))}
      <div className="rowline">
        <button type="button" className="btn primary" disabled={filled.length === 0} onClick={submit}>告诉{s.companionName || '它'}</button>
        <button type="button" className="btn" onClick={onDone}>先不了</button>
      </div>
    </div>
  );
}

export function MemoryView() {
  const app = useAppState();
  const navigate = useNavigate();
  const [intro, setIntro] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newKind, setNewKind] = useState<MemoryKind>('about_me');
  const byUpdated = (a: Memory, b: Memory) => b.updatedAt - a.updatedAt;
  const profile = app.memories.filter((m) => m.kind === 'about_me').sort((a, b) => b.importance - a.importance);
  const people = app.memories.filter((m) => m.kind === 'person').sort(byUpdated);
  const open = app.memories.filter((m) => (m.kind === 'followup' || m.kind === 'prediction') && m.status !== 'done').sort(byUpdated);
  const rest = app.memories.filter((m) => !profile.includes(m) && !people.includes(m) && !open.includes(m)).sort(byUpdated);
  const add = () => {
    void appStore.putMemory({ id: newId(), kind: newKind, text: newText.trim(), subjects: [], importance: 4, createdAt: Date.now(), updatedAt: Date.now(), sourceIds: [], ...(newKind === 'person' ? { topic: 'people' as const } : {}), ...(newKind === 'followup' || newKind === 'prediction' ? { status: 'open' as const } : {}) });
    setNewText('');
    setAdding(false);
  };
  const unknown = unknownTopics(app.memories);

  return (
    <div className="shell">
      <Top sub={app.learning ? '正在记……' : `${app.memories.length} 条`} />
      <div className="page">
        <p className="hint">每聊一轮，它会在后台把值得记的东西写下来。这里是全部。记错了就改，不想让它记就忘掉——它下次说话就不会再用了。</p>

        {unknown.length > 0 && (
          <section>
            <h2>它还不太知道</h2>
            {intro ? (
              <IntroForm onDone={() => { setIntro(false); navigate('/'); }} />
            ) : (
              <div className="card">
                <div className="hint">{unknown.map((t) => t.label).join('；')}。</div>
                <div className="rowline">
                  <button type="button" className="btn" onClick={() => setIntro(true)}>帮它认识你</button>
                  <span className="hint">聊着聊着它也会慢慢问；想一次说清楚就点这里。</span>
                </div>
              </div>
            )}
          </section>
        )}

        <section>
          <h2>你可以直接告诉它</h2>
          <div className="card">
            {adding ? (
              <>
                <textarea className="input" style={{ minHeight: 70 }} value={newText} autoFocus placeholder="比如：她最讨厌别人说「你想多了」。" onChange={(e) => setNewText(e.target.value)} />
                <div className="rowline">
                  <select className="input" style={{ width: 'auto' }} value={newKind} onChange={(e) => setNewKind(e.target.value as MemoryKind)}>
                    {MEMORY_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                  </select>
                  <button type="button" className="btn primary" disabled={!newText.trim()} onClick={add}>记住</button>
                  <button type="button" className="btn" onClick={() => setAdding(false)}>算了</button>
                </div>
              </>
            ) : (
              <div className="rowline">
                <button type="button" className="btn" onClick={() => setAdding(true)}>写一条让它记住</button>
                <span className="hint">不用等它自己发现。</span>
              </div>
            )}
          </div>
        </section>

        <Section title="关于她" count={profile.length} items={profile} hint="它还不了解她。多聊几次，或者上面直接写。" />
        <Section title="她生活里的人" count={people.length} items={people} />
        <Section title="记着要提的" count={open.length} items={open} hint="她说了要做的事，或它压着没说的话，会记在这里等时机。" />

        <section>
          <h2>日记<small>{app.journal.length} 天</small></h2>
          <div className="card journal">
            {app.journal.length === 0 ? <p className="hint">还没有。</p> : [...app.journal].reverse().slice(0, 30).map((j) => <div key={j.date}><b>{j.date}</b>{j.text}</div>)}
          </div>
        </section>

        <Section title="其他" count={rest.length} items={rest} />
      </div>
    </div>
  );
}
